// frontend/src/features/sites/sitesSlice.js
/*
Phase 8: Sites read-models are role-distinct:

- companySites: ACTIVE sites the company owns (UPS selector view)
- agencyAllowedSites: ACTIVE sites agency can operate on (contract-aware)
- upsAdminSites: ALL sites (active + inactive) for UPS Admin Manage Sites

selectedSiteId must remain valid only against operational lists.
IMPORTANT: upsAdminSites must NEVER affect selectedSiteId.
*/

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  fetchMyCompanySites,
  fetchMyAgencyAllowedSites,
  fetchUpsAdminSites,
} from '../../services/siteService';

// ---------------------------
// Thunks
// ---------------------------

export const loadMyCompanySites = createAsyncThunk(
  'sites/loadMyCompanySites',
  async () => {
    return await fetchMyCompanySites();
  }
);

export const loadMyAgencyAllowedSites = createAsyncThunk(
  'sites/loadMyAgencyAllowedSites',
  async () => {
    return await fetchMyAgencyAllowedSites();
  }
);

// ✅ UPS Admin list (includes inactive)
export const loadUpsAdminSites = createAsyncThunk(
  'sites/loadUpsAdminSites',
  async () => {
    return await fetchUpsAdminSites();
  }
);

// ---------------------------
// Initial State
// ---------------------------

const initialState = {
  companySites: [],
  agencyAllowedSites: [],
  upsAdminSites: [], // ✅ NEW
  selectedSiteId: null,
  loading: false,
  error: null,
};

// ---------------------------
// Helpers
// ---------------------------

function coerceId(v) {
  const n = v == null ? null : Number(v);
  return Number.isFinite(n) ? n : null;
}

function ensureSelectedIsValid(state, sites) {
  if (!state.selectedSiteId && sites.length > 0) {
    state.selectedSiteId = coerceId(sites[0]?.id);
    return;
  }

  if (
    state.selectedSiteId &&
    !sites.some((s) => coerceId(s.id) === state.selectedSiteId)
  ) {
    state.selectedSiteId = coerceId(sites[0]?.id);
  }
}

// ---------------------------
// Slice
// ---------------------------

const sitesSlice = createSlice({
  name: 'sites',
  initialState,
  reducers: {
    setSelectedSiteId(state, action) {
      state.selectedSiteId = coerceId(action.payload);
    },

    // Clear everything on logout/session change
    resetSites(state) {
      state.companySites = [];
      state.agencyAllowedSites = [];
      state.upsAdminSites = []; // ✅ NEW
      state.selectedSiteId = null;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers(builder) {
    builder

      // ----------------------------------
      // Company ACTIVE sites (UPS selector)
      // ----------------------------------
      .addCase(loadMyCompanySites.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadMyCompanySites.fulfilled, (state, action) => {
        state.loading = false;
        state.companySites = Array.isArray(action.payload) ? action.payload : [];
        ensureSelectedIsValid(state, state.companySites);
      })
      .addCase(loadMyCompanySites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || 'Failed to load company sites';
      })

      // ----------------------------------
      // Agency allowed ACTIVE sites
      // ----------------------------------
      .addCase(loadMyAgencyAllowedSites.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadMyAgencyAllowedSites.fulfilled, (state, action) => {
        state.loading = false;
        state.agencyAllowedSites = Array.isArray(action.payload) ? action.payload : [];
        ensureSelectedIsValid(state, state.agencyAllowedSites);
      })
      .addCase(loadMyAgencyAllowedSites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || 'Failed to load agency allowed sites';
      })

      // ----------------------------------
      // UPS Admin ALL sites (active + inactive)
      // ----------------------------------
      .addCase(loadUpsAdminSites.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadUpsAdminSites.fulfilled, (state, action) => {
        state.loading = false;
        state.upsAdminSites = Array.isArray(action.payload) ? action.payload : [];
        // ❗ DO NOT call ensureSelectedIsValid here.
        // Admin list must not influence operational selector state.
      })
      .addCase(loadUpsAdminSites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || 'Failed to load UPS admin sites';
      });
  },
});

// ---------------------------
// Exports
// ---------------------------

export const { setSelectedSiteId, resetSites } = sitesSlice.actions;
export default sitesSlice.reducer;

// Selectors

export const selectCompanySites = (s) => s.sites?.companySites || [];
export const selectAgencyAllowedSites = (s) => s.sites?.agencyAllowedSites || [];
export const selectUpsAdminSites = (s) => s.sites?.upsAdminSites || [];
export const selectSelectedSiteId = (s) => s.sites?.selectedSiteId ?? null;
export const selectSitesLoading = (s) => !!s.sites?.loading;
export const selectSitesError = (s) => s.sites?.error || null;
